import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "@/context/AuthContext";
import { getUserProfile, updateFavoriteSourceLanguages } from "@/lib/UserProfile";
import { updateWorkspaceFavoriteSourceLanguages } from "@/lib/Workspace";
import { normalizeFavoriteSourceLanguages, resolveFavoriteSourceLanguages } from "@/lib/favoriteSourceLanguages";
import { canonicalizeLanguageCode } from "@/lib/LanguageSupport";
export interface FavoriteSourceLanguagesContextValue {
  codes: string[];
  loading: boolean;
  saving: boolean;
  error: Error | null;
  canEdit: boolean;
  save: (codes: string[]) => Promise<void>;
}
const FavoriteSourceLanguagesContext = createContext<FavoriteSourceLanguagesContextValue | null>(null);
function normalizeCodes(codes: readonly unknown[] | null | undefined): string[] {
  return normalizeFavoriteSourceLanguages(codes?.map(code => canonicalizeLanguageCode(code) || code));
}
export function FavoriteSourceLanguagesProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user,
    loading: authLoading,
    activeWorkspaceId,
    activeWorkspace,
    workspacesLoading,
    refreshWorkspaces
  } = useAuthContext();
  const workspaceCodes = activeWorkspace?.workspace.favoriteSourceLanguages;
  const canEdit = Boolean(user?.uid) && (activeWorkspaceId === null || activeWorkspace?.role === "owner");
  const [codes, setCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setError(null);

    // Workspace memberships are restored from local cache before the callable
    // refresh completes. Use that cached workspace immediately instead of
    // blocking this lightweight preference view on billing-enriched refreshes.
    if (authLoading || activeWorkspaceId !== null && workspacesLoading && !activeWorkspace) {
      setLoading(true);
      return;
    }
    if (!user?.uid) {
      setCodes([]);
      setLoading(false);
      return;
    }
    if (activeWorkspaceId !== null) {
      setCodes(resolveFavoriteSourceLanguages({
        activeWorkspaceId,
        workspaceCodes
      }));
      setLoading(false);
      return;
    }
    setLoading(true);
    getUserProfile(user.uid).then(profile => {
      if (requestId !== requestIdRef.current) return;
      setCodes(resolveFavoriteSourceLanguages({
        activeWorkspaceId: null,
        personalCodes: profile?.favoriteSourceLanguages,
        personalLegacyCode: profile?.favoriteSourceLanguage
      }));
    }).catch((cause: unknown) => {
      if (requestId !== requestIdRef.current) return;
      setCodes([]);
      setError(cause instanceof Error ? cause : new Error("Failed to load favourite source languages"));
    }).finally(() => {
      if (requestId === requestIdRef.current) setLoading(false);
    });
  }, [activeWorkspace, activeWorkspaceId, authLoading, user?.uid, workspaceCodes, workspacesLoading]);
  const save = useCallback(async (nextCodes: string[]) => {
    if (!user?.uid) throw new Error("User is not authenticated");
    if (!canEdit) throw new Error("Only workspace owners can edit favourite source languages");
    const normalized = normalizeCodes(nextCodes);
    const previous = codes;
    setCodes(normalized);
    setSaving(true);
    setError(null);
    try {
      if (activeWorkspaceId !== null) {
        await updateWorkspaceFavoriteSourceLanguages(activeWorkspaceId, normalized);
        await refreshWorkspaces();
      } else {
        await updateFavoriteSourceLanguages(user.uid, normalized);
      }
    } catch (cause: unknown) {
      setCodes(previous);
      const nextError = cause instanceof Error ? cause : new Error("Failed to save favourite source languages");
      setError(nextError);
      throw nextError;
    } finally {
      setSaving(false);
    }
  }, [activeWorkspaceId, canEdit, codes, refreshWorkspaces, user?.uid]);
  const value = useMemo(() => ({
    codes,
    loading,
    saving,
    error,
    canEdit,
    save
  }), [canEdit, codes, error, loading, save, saving]);
  return <FavoriteSourceLanguagesContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="FavoriteSourceLanguagesProvider" data-sentry-source-file="FavoriteSourceLanguagesContext.tsx">
            {children}
        </FavoriteSourceLanguagesContext.Provider>;
}
export function useFavoriteSourceLanguages(): FavoriteSourceLanguagesContextValue {
  const context = useContext(FavoriteSourceLanguagesContext);
  if (!context) {
    throw new Error("useFavoriteSourceLanguages must be used inside FavoriteSourceLanguagesProvider");
  }
  return context;
}